import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ConnectionProviderService } from '@api/authentication';
import { ApiConnectionsModel, ApiConnectionsRepository, WorkspaceRepository } from '@database/central';
import { ModalController, NavController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { AlertService, ToastDuration, ToastService } from '@shared';
import { Subscription } from 'rxjs';
import { ConnectionsModalComponentsEnum, ConnectionTypeEnum } from '../../models';
import { ConnectionsModalState } from '../../services';

@Component({
	selector: 'app-connections-list',
	templateUrl: 'list.component.html',
	styleUrls: ['list.component.scss']
})
export class ConnectionsListComponent implements OnInit, OnDestroy {
	@Input() public connectionType: ConnectionTypeEnum;

	public connectionsList: ApiConnectionsModel[] | null = null;
	public connectionsSubscription: Subscription;

	public constructor(
		private navController: NavController,
		private alertService: AlertService,
		private translateService: TranslateService,
		private apiConnectionsRepository: ApiConnectionsRepository,
		private connectionsModalState: ConnectionsModalState,
		private modalCtrl: ModalController,
		private workspaceRepository: WorkspaceRepository,
		private toastService: ToastService,
		private connectionProviderService: ConnectionProviderService
	) {}

	public ngOnInit(): void {
		this.connectionsSubscription = this.apiConnectionsRepository
			.getConnections$()
			.subscribe((connections: ApiConnectionsModel[] | null) => (this.connectionsList = connections));
	}

	public goBack(): void {
		this.navController.pop();
	}

	public addNewConnection(): void {
		const scanQrCodeButton = {
			text: this.translateService.instant('connectionsModal.newConnectionOptions.qrCode'),
			handler: () =>
				this.connectionsModalState.setActive({
					componentName: ConnectionsModalComponentsEnum.addQrCodeConnection,
					connectionId: null
				})
		};

		const insertPathManuallyButton = {
			text: this.translateService.instant('connectionsModal.newConnectionOptions.pathManually'),
			handler: () =>
				this.connectionsModalState.setActive({
					componentName: ConnectionsModalComponentsEnum.addManuallyConnection,
					connectionId: null
				})
		};

		this.alertService.presentAlert('', [scanQrCodeButton, insertPathManuallyButton], true, 'new-connection');
	}

	public async selectConnection(connection: ApiConnectionsModel): Promise<void> {
		await this.connectionProviderService.save(connection.connectionId);
		if (ConnectionTypeEnum.includeNewOptions && !connection.selectedOptions) {
			this.connectionsModalState.setActive({
				componentName: ConnectionsModalComponentsEnum.editOptionsConnection,
				connectionId: connection.connectionId
			});
			return;
		}

		this.modalCtrl.dismiss(connection.connectionId);
	}

	public editConnection(connectionId: string): void {
		this.connectionsModalState.setActive({
			componentName: ConnectionsModalComponentsEnum.editConnection,
			connectionId
		});
	}

	public async deleteConnection(connectionId: string): Promise<void> {
		const cancelButton = {
			text: this.translateService.instant('alert.buttons.keepRecord')
		};

		const confirmButton = {
			text: this.translateService.instant('alert.buttons.deleteRecord'),
			handler: async () => {
				try {
					await this.apiConnectionsRepository.deleteConnection(connectionId);
					await this.workspaceRepository.removeConnectionId(connectionId);

					await this.toastService.presentSuccess(
						this.translateService.instant('connectionsModal.form.list.success.delete'),
						ToastDuration.Fast
					);
				} catch (error) {
					await this.toastService.presentError(
						this.translateService.instant('connectionsModal.form.list.errors.delete'),
						ToastDuration.Fast
					);
				}
			}
		};

		await this.alertService.presentAlert(
			this.translateService.instant('alert.deleteConnectionMessage'),
			[cancelButton, confirmButton],
			false
		);
	}

	public ngOnDestroy(): void {
		this.connectionsSubscription.unsubscribe();
	}
}
